import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Send, Tag, Layers } from 'lucide-react';

export type ThemeColor = 'blue' | 'purple' | 'emerald' | 'orange' | 'rose' | 'cyan' | 'default';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  category?: string;
  moduleName?: string;
  themeColor?: ThemeColor;
  t: (key: string) => string;
}

export function ContactModal({ isOpen, onClose, title, category, moduleName, themeColor = 'default', t }: ContactModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would send the data to a backend
    console.log('Form submitted:', { ...formData, category, moduleName });
    alert(t('Thank you for your interest! We will get back to you soon.'));
    onClose();
    setFormData({ name: '', email: '', phone: '', interest: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const themeStyles = {
    blue: { text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30', focus: 'focus:border-blue-500 focus:ring-blue-500', button: 'bg-blue-600 hover:bg-blue-700 text-white' },
    purple: { text: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30', focus: 'focus:border-purple-500 focus:ring-purple-500', button: 'bg-purple-600 hover:bg-purple-700 text-white' },
    emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', focus: 'focus:border-emerald-500 focus:ring-emerald-500', button: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
    orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/30', focus: 'focus:border-orange-500 focus:ring-orange-500', button: 'bg-orange-600 hover:bg-orange-700 text-white' },
    rose: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30', focus: 'focus:border-rose-500 focus:ring-rose-500', button: 'bg-rose-600 hover:bg-rose-700 text-white' },
    cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', focus: 'focus:border-cyan-500 focus:ring-cyan-500', button: 'bg-cyan-600 hover:bg-cyan-700 text-white' },
    default: { text: 'text-white', bg: 'bg-white/5', border: 'border-white/20', focus: 'focus:border-white focus:ring-white', button: 'bg-white hover:bg-gray-200 text-black' },
  };

  const currentTheme = themeStyles[themeColor] || themeStyles.default;

  return (
    <AnimatePresence>
      {isOpen && (
        <div key="modal-wrapper" className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg z-[101]"
          >
            <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden relative flex flex-col max-h-[90vh]">
              {/* Header */}
              <div className="p-6 border-b border-white/10 flex justify-between items-center bg-white/5 shrink-0">
                <h2 className="text-2xl font-bold text-white">{title}</h2>
                <button 
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Context Info */}
              {(category || moduleName) && (
                <div className={`p-4 border-b border-white/10 flex flex-col gap-2 shrink-0 ${currentTheme.bg}`}>
                  {category && (
                    <div className="flex items-center gap-2">
                      <Tag className={`w-4 h-4 ${currentTheme.text}`} />
                      <span className="text-sm font-medium text-white/70">{t("Category:")} <strong className={currentTheme.text}>{category}</strong></span>
                    </div>
                  )}
                  {moduleName && (
                    <div className="flex items-center gap-2">
                      <Layers className={`w-4 h-4 ${currentTheme.text}`} />
                      <span className="text-sm font-medium text-white/70">{t("Module:")} <strong className={currentTheme.text}>{moduleName}</strong></span>
                    </div>
                  )}
                </div>
              )}

              {/* Form */}
              <div className="overflow-y-auto p-6">
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="text-sm font-medium text-white/70">{t("Name")}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className={`bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-all ${currentTheme.focus}`}
                    placeholder={t("John Doe")}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-sm font-medium text-white/70">{t("Email")}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className={`bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-all ${currentTheme.focus}`}
                      placeholder={t("john@example.com")}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="phone" className="text-sm font-medium text-white/70">{t("Phone Number")}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-all ${currentTheme.focus}`}
                      placeholder={t("+852 1234 5678")}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="interest" className="text-sm font-medium text-white/70">{t("What would you like to do?")}</label>
                  <textarea
                    id="interest"
                    name="interest"
                    required
                    value={formData.interest}
                    onChange={handleChange}
                    rows={4}
                    className={`bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none transition-all resize-none ${currentTheme.focus}`}
                    placeholder={t("Tell us about your goals or how we can help...")}
                  />
                </div>

                <button
                  type="submit"
                  className={`mt-4 w-full font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${currentTheme.button}`}
                >
                  <span>{t("Submit Request")}</span>
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
